import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PermissionsService } from '../services/permissions/permissions.service';
import {
  TPermissionCategory,
  TPermissionKey,
} from '../../utils/rolesDataModule';

/**
 * Structural directive to conditionally show/hide elements based on user permissions
 *
 * Usage examples:
 *
 * Single permission check:
 * <button *hasPermission="{ category: 'USER_MANAGEMENT', permission: 'create-users' }">
 *   Create User
 * </button>
 *
 * Check any permission from a list:
 * <div *hasPermission="{ category: 'USER_MANAGEMENT', anyOf: ['edit-users', 'delete-users'] }">
 *   User actions...
 * </div>
 *
 * Check all permissions from a list:
 * <div *hasPermission="{ category: 'USER_MANAGEMENT', allOf: ['view-users', 'edit-users'] }">
 *   Advanced user management...
 * </div>
 *
 * There is no `else` branch: for a fallback, check PermissionsService in the
 * component and use @if / @else in the template.
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private permissionsService = inject(PermissionsService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);

  private destroy$ = new Subject<void>();
  private hasView = false;

  @Input() hasPermission:
    | {
        category: TPermissionCategory;
        permission: TPermissionKey;
      }
    | {
        category: TPermissionCategory;
        anyOf: TPermissionKey[];
      }
    | {
        category: TPermissionCategory;
        allOf: TPermissionKey[];
      } = { category: 'USER_MANAGEMENT', permission: 'view-users' };

  ngOnInit(): void {
    this.checkPermission();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkPermission(): void {
    const config = this.hasPermission;

    if ('permission' in config) {
      // Single permission check
      this.permissionsService
        .hasPermission(config.category, config.permission)
        .pipe(takeUntil(this.destroy$))
        .subscribe((hasPermission) => {
          this.updateView(hasPermission);
        });
    } else if ('anyOf' in config) {
      // Check if user has any of the permissions
      this.permissionsService
        .hasAnyPermission(config.category, config.anyOf)
        .pipe(takeUntil(this.destroy$))
        .subscribe((hasPermission) => {
          this.updateView(hasPermission);
        });
    } else if ('allOf' in config) {
      // Check if user has all of the permissions
      this.permissionsService
        .hasAllPermissions(config.category, config.allOf)
        .pipe(takeUntil(this.destroy$))
        .subscribe((hasPermission) => {
          this.updateView(hasPermission);
        });
    }
  }

  private updateView(hasPermission: boolean): void {
    if (hasPermission && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasPermission && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
